import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { PARAM_FILTER, QUERY_KEY } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useUserInfo } from '../../hooks/useUserInfo'

import { getDepositAccount } from '../../api/depositAccount'
import { getCashTransaction } from '../../api/my'
import type { GetCashTransactionParams } from '../../api/my'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { CenterWrap, AlignCenterItem, ColumnItem, RowBetween } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { SlideFilter } from '../../components/paramOption/SlideFilter'
import { Sort } from '../../components/paramOption/Sort'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'

import { Color } from '../../theme/color'
import type { ColorKeys } from '../../theme/color'

const FILTER_LIST: { type: GetCashTransactionParams['transactionType']; title: string }[] = [
  { type: undefined, title: '전체' },
  { type: PARAM_FILTER.SALE, title: '청약 신청' },
  { type: PARAM_FILTER.DEPOSIT, title: '예치금 충전' },
  { type: PARAM_FILTER.WITHDRAWAL, title: '예치금 출금' },
  { type: PARAM_FILTER.SETTLEMENT, title: '매각 대금' },
  { type: PARAM_FILTER.UNALLOCATED, title: '환불' },
  { type: PARAM_FILTER.REFUND, title: '청약 취소' },
]

const SORT_LIST: { type: GetCashTransactionParams['sort']; title: string }[] = [
  { type: 'createdDate,desc', title: '최근 거래순' },
  { type: 'createdDate,asc', title: '오래된 거래순' },
]

export function Account() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const [sort, setSortType] = useState<GetCashTransactionParams['sort']>('createdDate,desc')
  const [transactionType, setTransactionType] = useState<GetCashTransactionParams['transactionType'] | undefined>()

  const { isLogin, isCheckingLogin } = useUserInfo()

  const depositAccount = useQuery({ queryKey: [QUERY_KEY.depositAccount], queryFn: getDepositAccount })
  const cashTransaction = useInfiniteQuery({
    queryKey: [QUERY_KEY.cashTransaction, { transactionType, sort }],
    queryFn: async ({ pageParam = 0 }) =>
      await getCashTransaction({ offset: pageParam, size: 50, transactionType, sort }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (depositAccount.isError && depositAccount.error) {
      if (isAxiosError(depositAccount.error)) {
        toast(depositAccount.error.response?.data.message, depositAccount.error)
      }
    }
  }, [depositAccount.isError, depositAccount.error])

  useEffect(() => {
    if (cashTransaction.isError && cashTransaction.error) {
      if (isAxiosError(cashTransaction.error)) {
        toast(cashTransaction.error.response?.data.message, cashTransaction.error)
      }
    }
  }, [cashTransaction.isError, cashTransaction.error])

  if (depositAccount.isPending || depositAccount.isError || cashTransaction.isPending || cashTransaction.isError) {
    return (
      <Wrap>
        <Header type="SUB" title="입출금 내역" />
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  return (
    <Wrap>
      <Header type="SUB" title="입출금 내역" />
      <StyledContent id="infinity-scroll-target">
        <AccountInfoWrap>
          <AccountCard>
            <RowBetween>
              <AlignCenterItem $gap={8}>
                <BankIcon name="NH농협은행" size={24} />
                <Text size={16} weight={500} lineHeight={24}>
                  NH농협은행
                </Text>
              </AlignCenterItem>
              <Text size={14} lineHeight={22} color="text_secondary">
                {depositAccount.data.account}
              </Text>
            </RowBetween>
            <DivideLine />
            <UserAccountInfo>
              <Text size={14} lineHeight={22}>
                <Text size={14} weight={600}>
                  {depositAccount.data.bankOwner}님
                </Text>
                의 예치금
              </Text>
              <Text size={24} weight={600} lineHeight={34} color="primary_5">
                {depositAccount.data.balance.toLocaleString()}원
              </Text>
            </UserAccountInfo>
            <AlignCenterItem $gap={8}>
              <Button type="secondary" size="small" block={true} href="/myPage/account/withdrawal">
                출금하기
              </Button>
              <Button type="primary" size="small" block={true} href="/myPage/account/deposit">
                충전하기
              </Button>
            </AlignCenterItem>
          </AccountCard>
        </AccountInfoWrap>
        <FilterContainer>
          <SlideFilter<GetCashTransactionParams['transactionType']>
            filterType={transactionType}
            filterList={FILTER_LIST}
            setFilterType={setTransactionType}
          />
        </FilterContainer>
        <SortContainer>
          <Sort<GetCashTransactionParams['sort']> sortType={sort} sortList={SORT_LIST} setSortType={setSortType} />
        </SortContainer>
        {cashTransaction.data.pages.length ? (
          <InfinityScroll
            length={cashTransaction.data.pages.length}
            isLast={!cashTransaction.hasNextPage}
            next={cashTransaction.fetchNextPage}
          >
            <ColumnItem>
              {cashTransaction.data.pages.map(
                ({ id, createdDate, transactionType, purchaseTypeLabel, orderSequence, amount, balance, hasFee }) => {
                  const YMD = dayjs(createdDate).format('YY.MM.DD')
                  const HM = dayjs(createdDate).format('HH:mm')

                  const isWithdrawal = String(amount).includes('-')

                  const textColor: ColorKeys = isWithdrawal ? 'primary_5' : 'error'

                  return (
                    <BreakdownContainer key={id}>
                      <TransactionContainer>
                        <ColumnItem $gap={6}>
                          <Text size={10} color="text_secondary">
                            {YMD}
                          </Text>
                          <Text size={10} color="text_secondary">
                            {HM}
                          </Text>
                        </ColumnItem>
                        <ColumnItem $gap={4}>
                          <RowBetween>
                            <Text size={16} weight={500}>
                              {purchaseTypeLabel}
                            </Text>
                            <ColumnItem>
                              <RowBetween>
                                <Text size={18} weight={600} color={textColor} $textAlign="right">
                                  {isWithdrawal ? amount.toLocaleString() : `+${amount.toLocaleString()}`}원
                                </Text>
                              </RowBetween>
                            </ColumnItem>
                          </RowBetween>
                          <AmountInfoBox>
                            {(transactionType === 'SALE' || transactionType === 'REFUND') && (
                              <Text size={10}>거래ID: {orderSequence}</Text>
                            )}
                            <AmountDescription>
                              {hasFee && (
                                <Text size={10} color="text_secondary">
                                  (수수료 포함)
                                </Text>
                              )}
                              <Text size={10} color="text_secondary">
                                {balance.toLocaleString()}원
                              </Text>
                            </AmountDescription>
                          </AmountInfoBox>
                        </ColumnItem>
                      </TransactionContainer>
                    </BreakdownContainer>
                  )
                },
              )}
            </ColumnItem>
          </InfinityScroll>
        ) : (
          <EmptyBox>
            <Text size={14} lineHeight={22} color="text_secondary" $textAlign="center">
              조회 가능한 내역이 없습니다.
            </Text>
          </EmptyBox>
        )}
      </StyledContent>
    </Wrap>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: 16px;
`

const EmptyBox = styled.div`
  text-align: center;
  width: 100%;
  padding: 120px 0;
`

const AccountInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const AccountCard = styled(ColumnItem)`
  gap: 8px;
  padding: 24px;
  border-radius: 16px;
  background-color: ${Color.white};
`

const UserAccountInfo = styled(ColumnItem)`
  margin: 8px 0;
`

const FilterContainer = styled.div`
  position: sticky;
  top: 0;
  padding: 16px 0;
  background-color: ${Color.white};
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 16px;
  padding-bottom: 16px;
`

const BreakdownContainer = styled.div`
  padding: 0 24px;
`

const TransactionContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  padding: 16px 0;
  border-bottom: 1px solid ${Color.line};
  > *:first-child {
    flex-shrink: 0;
  }
  > *:last-child {
    width: 100%;
  }
`

const AmountInfoBox = styled(RowBetween)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  > *:first-child {
    flex-grow: 1;
    width: 100%;
  }
`

const AmountDescription = styled(ColumnItem)`
  flex-grow: 0;
  flex-shrink: 0;
  align-items: flex-end;
  gap: 6px;
`
